/**
 * TypeScript mirror of the WP7 whiteboard + images contract (`docs/specs/WP7-whiteboard.md`):
 * REST snapshot/clear for the shared Excalidraw board, room-scoped image upload/fetch, and the
 * pure reconciliation rule the server and every client apply identically.
 *
 * Deliberately excalidraw-agnostic: `reconcile`/`diffChanged`/`versionsOf` are generic over any
 * shape carrying `id`/`version`/`versionNonce`, so they're plain, fast-to-test functions that don't
 * need a real `ExcalidrawImperativeAPI` — `components/whiteboard/useExcalidrawSync.ts` is what
 * bridges this to the actual Excalidraw element types.
 */
import { apiGet, apiSend, ApiError, readCookie } from './api';

/** The minimal shape the reconciliation rule needs — a real Excalidraw element satisfies this
 * structurally, so callers can pass `OrderedExcalidrawElement[]` straight through. */
export interface Reconcilable {
  id: string;
  version: number;
  versionNonce: number;
}

export interface WhiteboardFile {
  fileId: string;
  assetId: number;
  mime: string;
}

/** `GET /api/rooms/{id}/whiteboard`. `elements`/`appState` are typed loosely (`unknown`-ish JSON)
 * here on purpose — this module never depends on `@excalidraw/excalidraw`'s types; the sync hook
 * casts them to the real element type at the one place that needs it. */
export interface WhiteboardSnapshot {
  elements: (Reconcilable & Record<string, unknown>)[];
  appState: { viewBackgroundColor?: string; gridSize?: number | null };
  version: number;
  files: WhiteboardFile[];
}

export function whiteboardQuery(roomId: number) {
  return {
    queryKey: ['rooms', roomId, 'whiteboard'] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<WhiteboardSnapshot>(`/api/rooms/${roomId}/whiteboard`, signal),
  };
}

/** HOST-only clear (contract: `DELETE /api/rooms/{id}/whiteboard`) — broadcasts a `reset` on the
 * `whiteboard` topic that every subscriber (including the caller) applies identically. */
export function clearWhiteboard(vars: { roomId: number }): Promise<void> {
  return apiSend('DELETE', `/api/rooms/${vars.roomId}/whiteboard`);
}

export interface ImageAsset {
  id: number;
  fileId: string;
  mime: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  url: string;
  uploadedAt: string;
}

/**
 * `POST /api/rooms/{id}/images`, multipart. Not routed through `lib/api.ts`'s `apiSend` — that
 * helper always JSON-encodes its body — so this does its own `fetch` with a `FormData` body, the
 * same CSRF-cookie-to-header echo (`readCookie`/`X-XSRF-TOKEN`) and same-origin credentials as
 * every other mutation.
 */
export async function uploadImage(roomId: number, file: File | Blob, fileId?: string): Promise<ImageAsset> {
  const formData = new FormData();
  formData.append('file', file);
  if (fileId) {
    formData.append('fileId', fileId);
  }
  const token = readCookie('XSRF-TOKEN');
  const response = await fetch(`/api/rooms/${roomId}/images`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(token ? { 'X-XSRF-TOKEN': token } : {}) },
    body: formData,
  });

  if (!response.ok) {
    let body: { error?: string; message?: string } | undefined;
    try {
      body = (await response.json()) as { error?: string; message?: string };
    } catch {
      // Not every error response has a JSON body — fall back to the generic message below.
    }
    throw new ApiError(`POST /api/rooms/${roomId}/images failed with ${response.status}`, response.status, body);
  }
  return (await response.json()) as ImageAsset;
}

/** A friendly message for the well-known upload error statuses the contract documents (413/415/409);
 * anything else falls back to the server's own message or a generic one. */
export function describeUploadError(error: ApiError): string {
  switch (error.status) {
    case 413:
      return 'That image is larger than the 10 MiB limit.';
    case 415:
      return "That file isn't a supported image type (PNG, JPEG, GIF, or WEBP).";
    case 409:
      return 'This room has reached its image quota.';
    default:
      return 'Could not upload that image.';
  }
}

export function imageUrl(roomId: number, assetId: number): string {
  return `/api/rooms/${roomId}/images/${assetId}`;
}

export function imageByFileUrl(roomId: number, fileId: string): string {
  return `/api/rooms/${roomId}/images/by-file/${encodeURIComponent(fileId)}`;
}

/** The overlay's `permitAll` counterpart (WP6 contract) — token-authenticated, no session. */
export function overlayImageUrl(token: string, assetId: number): string {
  return `/api/overlay/${encodeURIComponent(token)}/images/${assetId}`;
}

/**
 * Mirrors the server's reconciliation rule (`docs/specs/WP7-whiteboard.md`): a `remote` element
 * replaces the stored `local` one when its `version` is higher, or equal with a lower
 * `versionNonce` (a deterministic tie-break for two concurrent edits landing with the same
 * version); otherwise `remote` is ignored. Deleted elements (`isDeleted: true`) are ordinary
 * elements as far as this rule is concerned — the server is what actually compacts them away after
 * they've had time to reach every peer.
 *
 * Not Excalidraw's own exported `reconcileElements`: that function requires a full `AppState` (to
 * special-case an element the *local* user is actively editing/resizing/drawing) — more than this
 * pure, unit-testable shape needs, and more than the documented server-mirroring contract asks for.
 * This implements exactly the version/versionNonce rule the spec documents, nothing more.
 */
export function reconcile<T extends Reconcilable>(local: readonly T[], remote: readonly T[]): T[] {
  const localById = new Map(local.map((element) => [element.id, element] as const));
  const remoteById = new Map(remote.map((element) => [element.id, element] as const));

  const merged = local.map((element) => {
    const incoming = remoteById.get(element.id);
    return incoming && shouldReplace(element, incoming) ? incoming : element;
  });
  for (const element of remote) {
    if (!localById.has(element.id)) {
      merged.push(element);
    }
  }
  return merged;
}

function shouldReplace(local: Reconcilable, remote: Reconcilable): boolean {
  if (remote.version > local.version) {
    return true;
  }
  return remote.version === local.version && remote.versionNonce < local.versionNonce;
}

/** The elements whose `version` has advanced past what `prevVersions` last recorded for that id —
 * i.e. the ones a local edit actually changed since the last publish (a brand-new element, absent
 * from `prevVersions`, always counts as changed). */
export function diffChanged<T extends Reconcilable>(prevVersions: ReadonlyMap<string, number>, elements: readonly T[]): T[] {
  return elements.filter((element) => {
    const prev = prevVersions.get(element.id);
    return prev === undefined || element.version > prev;
  });
}

/** Snapshots `id -> version` for a set of elements, for `diffChanged`'s `prevVersions` bookkeeping. */
export function versionsOf(elements: readonly Reconcilable[]): Map<string, number> {
  return new Map(elements.map((element) => [element.id, element.version]));
}
